import React, { useState } from 'react';
import styled from 'styled-components';

interface CalendarComponentProps {
  selectedDate: string;
  onDateSelect: (date: string) => void;
}

// 달력 컨테이너 (포인트 코인 조회 페이지와 동일)
const CalendarContainer = styled.div`
  width: 100%;
`;

// 달력 헤더 (포인트 코인 조회 페이지와 동일)
const CalendarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const CalendarNavButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  transition: all 0.2s ease;
  color: var(--color-text-primary);

  &:hover {
    background-color: rgba(var(--color-accent-rgb), 0.1);
  }
  
  /* 다크모드: 호버 시 배경색을 회색으로 */
  :root:not([data-theme='light']) &:hover {
    background-color: rgba(128, 128, 128, 0.2);
  }
`;

const CalendarTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text-primary);
`;

// 년월 입력 컨테이너 (포인트 코인 조회 페이지와 동일)
const YearMonthInputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// 년월 입력 필드 (포인트 코인 조회 페이지와 동일)
const YearMonthInput = styled.input`
  width: 60px;
  padding: 4px 8px;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  font-size: 14px;
  text-align: center;
  
  &:focus {
    outline: none;
    border-color: rgba(var(--color-accent-rgb), 0.5);
  }
  
  /* 다크모드: 포커스 시 테두리 색상을 회색으로 */
  :root:not([data-theme='light']) &:focus {
    border-color: #505050;
  }
`;

// 오늘 버튼 컨테이너 (포인트 코인 조회 페이지와 동일)
const TodayButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
`;

// 오늘 버튼 (포인트 코인 조회 페이지와 동일)
const TodayButton = styled.button`
  padding: 8px 16px;
  background: var(--color-accent-strong);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 30px;
  
  &:hover {
    background: rgba(var(--color-accent-rgb), 0.8);
  }
  
  /* 다크모드: 배경색을 회색으로 */
  :root:not([data-theme='light']) & {
    background: #505050;
  }
  
  /* 다크모드: 호버 시 배경색을 밝은 회색으로 */
  :root:not([data-theme='light']) &:hover {
    background: #606060;
  }
`;

// 달력 그리드 (포인트 코인 조회 페이지와 동일)
const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
`;

const CalendarDayHeader = styled.div<{ $isSunday?: boolean }>`
  padding: 8px;
  text-align: center;
  font-weight: 600;
  color: ${props => props.$isSunday ? '#dc3545' : 'var(--color-text-gray)'};
  font-size: 14px;
`;

const CalendarDay = styled.div<{ $isToday?: boolean; $isSelected?: boolean; $isSunday?: boolean }>`
  padding: 8px;
  text-align: center;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s ease;
  font-size: 14px;
  min-height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  background: ${props => {
    if (props.$isSelected) return '#10504A';
    if (props.$isToday) return 'rgba(var(--color-accent-rgb), 0.1)';
    return 'transparent';
  }};

  color: ${props => {
    if (props.$isSelected) return 'white';
    if (props.$isToday) return '#10504A';
    if (props.$isSunday) return '#dc3545';
    return 'var(--color-text-primary)';
  }};

  &:hover {
    background: ${props => {
      if (props.$isSelected) return '#0D4039';
      return 'rgba(var(--color-accent-rgb), 0.1)';
    }};
  }
  
  /* 다크모드: 선택된 날짜 배경색을 회색으로 */
  :root:not([data-theme='light']) & {
    ${props => props.$isSelected && `
      background: #505050 !important;
      color: white !important;
    `}
    
    ${props => props.$isToday && !props.$isSelected && `
      background: rgba(128, 128, 128, 0.2) !important;
      color: #ffffff !important;
    `}
  }
  
  /* 다크모드: 호버 시 배경색 */
  :root:not([data-theme='light']) &:hover {
    ${props => props.$isSelected && `
      background: #606060 !important;
    `}
    
    ${props => !props.$isSelected && `
      background: rgba(128, 128, 128, 0.2) !important;
    `}
  }
`;

export const CalendarComponent: React.FC<CalendarComponentProps> = ({ selectedDate, onDateSelect }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isEditingYearMonth, setIsEditingYearMonth] = useState(false);
  const [tempYear, setTempYear] = useState('');
  const [tempMonth, setTempMonth] = useState('');
  const selectedDateObj = new Date(selectedDate);
  
  const today = new Date();
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  
  // 해당 월의 첫 번째 날과 마지막 날
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDayOfWeek = firstDay.getDay();
  
  // 이전 달로 이동
  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(year, month - 1, 1));
  };
  
  // 다음 달로 이동
  const goToNextMonth = () => {
    setCurrentMonth(new Date(year, month + 1, 1));
  };
  
  // 오늘 날짜 선택
  const selectToday = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const todayString = `${year}-${month}-${day}`;
    onDateSelect(todayString);
  };
  
  // 년월 직접 입력 시작
  const startEditingYearMonth = () => {
    setIsEditingYearMonth(true);
    setTempYear(year.toString());
    setTempMonth((month + 1).toString());
  };
  
  // 년월 입력 완료 (Enter 키 또는 포커스 아웃)
  const finishEditingYearMonth = () => {
    const newYear = parseInt(tempYear);
    const newMonth = parseInt(tempMonth) - 1; // JavaScript month는 0부터 시작
    
    if (newYear >= 1900 && newYear <= 2100 && newMonth >= 0 && newMonth <= 11) {
      setCurrentMonth(new Date(newYear, newMonth, 1));
    }
    setIsEditingYearMonth(false);
  };
  
  // 년월 입력 취소 (Escape 키)
  const cancelEditingYearMonth = () => {
    setIsEditingYearMonth(false);
    setTempYear('');
    setTempMonth('');
  };
  
  // 년도 입력 핸들러
  const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTempYear(e.target.value);
  };
  
  // 월 입력 핸들러
  const handleMonthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTempMonth(e.target.value);
  };
  
  // 키보드 이벤트 핸들러
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      finishEditingYearMonth();
    } else if (e.key === 'Escape') {
      cancelEditingYearMonth();
    }
  };
  
  // 날짜 선택
  const handleDateClick = (day: number) => {
    const selectedDate = new Date(year, month, day);
    // 로컬 시간대를 사용하여 YYYY-MM-DD 형식으로 포맷
    const yearStr = selectedDate.getFullYear();
    const monthStr = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const dayStr = String(selectedDate.getDate()).padStart(2, '0');
    const formattedDate = `${yearStr}-${monthStr}-${dayStr}`;
    onDateSelect(formattedDate);
  };
  
  // 요일 헤더 (포인트 코인 조회 페이지와 동일)
  const dayNames = ['일', '월', '화', '수', '목', '금', '토'];
  
  // 달력 그리드 생성 (포인트 코인 조회 페이지와 동일)
  const calendarDays = [];
  
  // 이전 달의 빈 칸들
  for (let i = 0; i < startingDayOfWeek; i++) {
    calendarDays.push(<CalendarDay key={`empty-${i}`} />);
  }
  
  // 현재 달의 날짜들
  for (let day = 1; day <= daysInMonth; day++) {
    const isToday = year === today.getFullYear() && month === today.getMonth() && day === today.getDate();
    const isSelected = year === selectedDateObj.getFullYear() && month === selectedDateObj.getMonth() && day === selectedDateObj.getDate();
    const dayOfWeek = new Date(year, month, day).getDay();
    const isSunday = dayOfWeek === 0;
    
    calendarDays.push(
      <CalendarDay
        key={day}
        $isToday={isToday}
        $isSelected={isSelected}
        $isSunday={isSunday}
        onClick={() => handleDateClick(day)}
      >
        {day}
      </CalendarDay>
    );
  }
  
  return (
    <CalendarContainer>
      <CalendarHeader>
        <CalendarNavButton onClick={goToPreviousMonth}>‹</CalendarNavButton>
        {isEditingYearMonth ? (
          <YearMonthInputContainer>
            <YearMonthInput
              type="number"
              value={tempYear}
              onChange={handleYearChange}
              onBlur={finishEditingYearMonth}
              onKeyDown={handleKeyPress}
              placeholder="년도"
              min="1900"
              max="2100"
              autoFocus
            />
            <span style={{ margin: '0 4px', color: 'var(--color-text-gray)' }}>년</span>
            <YearMonthInput
              type="number"
              value={tempMonth}
              onChange={handleMonthChange}
              onBlur={finishEditingYearMonth}
              onKeyDown={handleKeyPress}
              placeholder="월"
              min="1"
              max="12"
            />
            <span style={{ margin: '0 4px', color: 'var(--color-text-gray)' }}>월</span>
          </YearMonthInputContainer>
        ) : (
          <CalendarTitle onClick={startEditingYearMonth} style={{ cursor: 'pointer' }}>
            {year}년 {month + 1}월
          </CalendarTitle>
        )}
        <CalendarNavButton onClick={goToNextMonth}>›</CalendarNavButton>
      </CalendarHeader>
      
      <TodayButtonContainer>
        <TodayButton onClick={selectToday}>오늘</TodayButton>
      </TodayButtonContainer>
      
      <CalendarGrid>
        {dayNames.map((dayName, index) => (
          <CalendarDayHeader key={dayName} $isSunday={index === 0}>{dayName}</CalendarDayHeader>
        ))}
        {calendarDays}
      </CalendarGrid>
    </CalendarContainer>
  );
};